import { useMemo } from 'react';
import { addMinutes, isPast } from 'date-fns';
import { PARTICIPANT_ROLES, USER_TYPES } from '@medflex/utils/constants';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import { RequestTypes } from '@medflex/shared-types';
import useMessages from './useMessages';
import usePamRequests from './usePamRequests';
import useSessionMeetings from './useSessionMeetings';
import useUserProfile from './useUserProfile';
import useSelectedContact from './useSelectedContact';
import isSelf from '../../utils/isSelf';
import useSessionCalls from './useSessionCalls';

const normalizeSessions = ({ sessions, userProfile }) =>
  sessions.map(session => {
    const moderator = session.participants.find(
      participant => Array.isArray(participant.roles) && participant.roles.includes('moderator'),
    );
    const isSender = moderator.userId === userProfile.id;
    const isOwner =
      userProfile.type !== USER_TYPES.PATIENT && session.physicianUserId === userProfile.id;
    const isInSenderPractice =
      isOwner ||
      (userProfile.type !== USER_TYPES.PATIENT &&
        session.physicianUserId === userProfile.attributes.physicianUserId);
    const joinable =
      isSender ||
      session.participants.some(
        participant =>
          participant.userId === userProfile.id &&
          participant.roles?.toString() !== PARTICIPANT_ROLES.ORGANIZER,
      );

    const time =
      session.scheduledAt && new Date(session.scheduledAt) < new Date()
        ? session.scheduledAt
        : session.createdAt;

    let { endedAt } = session;
    if (
      !endedAt &&
      (isPast(addMinutes(new Date(session.lastTouchedAt), 10)) ||
        isPast(new Date(session.scheduledEndedAt)))
    ) {
      endedAt = session.lastTouchedAt;
    }

    return {
      ...session,
      endedAt,
      type: `session__${session.type}`,
      time,
      isSender,
      isInSenderPractice,
      sender: { id: moderator.userId },
      user: {
        ...moderator,
        initials: `${moderator.firstName[0]}${moderator.lastName[0]}`,
        fullNameWithTitle: getFullNameWithTitle(moderator),
        jobTitle: moderator.workArea1 || moderator.jobTitle,
      },
      isPatient: userProfile.type === USER_TYPES.PATIENT,
      joinable,
    };
  });

const sortMessagesByTime = (a, b) => {
  if (a.time > b.time) return 1;
  if (a.time < b.time) return -1;
  return 0;
};

const filterOutRequestTypes = requests =>
  requests.filter(({ data }) => ![RequestTypes.DOCUMENT].includes(data.requestType));

const useMergedMessages = (isPatient = false) => {
  const { messages } = useMessages();
  const { meetings } = useSessionMeetings();
  const { calls } = useSessionCalls();
  const { userProfile } = useUserProfile();
  const { selectedContact } = useSelectedContact();
  const pamRequests = usePamRequests(selectedContact?.id ? [selectedContact.id] : []);
  const selectedChatRequests = isPatient ? [] : pamRequests[selectedContact?.id]?.requests;

  const sessionMessages = useMemo(() => {
    if ((meetings || calls) && selectedContact && userProfile) {
      const participatingSessions = [...meetings, ...calls].filter(s =>
        s.participants.some(
          p =>
            p.userId === selectedContact.user.id ||
            (p.reference && p.reference === selectedContact.user.email) || // to catch sessions invites sent anonymously via email (see MFLEX-3533)
            (p.physicianUserId && p.physicianUserId === selectedContact.user.id),
        ),
      );
      return normalizeSessions({ sessions: participatingSessions, userProfile });
    }
    return [];
  }, [meetings, calls, selectedContact, userProfile]);

  const mergedMessages = useMemo(() => {
    const messagesEnhanced = messages.map(m => ({
      ...m,
      isSender:
        !m.sender.meOrSurrogate ||
        (selectedContact && (isSelf(selectedContact.user) || selectedContact.mainContactId))
          ? m.isSender
          : m.sender.meOrSurrogate,
    }));

    if (sessionMessages?.length || selectedChatRequests?.length) {
      return [
        ...messagesEnhanced,
        ...(sessionMessages?.length ? sessionMessages : []),
        ...(selectedChatRequests?.length ? filterOutRequestTypes(selectedChatRequests) : []),
      ];
    }

    return messagesEnhanced;
  }, [messages, pamRequests, selectedContact, sessionMessages]);

  const sortedMessages = useMemo(() => {
    return mergedMessages.sort(sortMessagesByTime);
  }, [mergedMessages]);

  return sortedMessages;
};

export default useMergedMessages;
